/**
 * **The Grunt template's two engine halves** (`npc-gaps` 2.3, Core p.322). Its Traits fold their
 * numbers at spawn (Chaff: 1 HP; Weak: 1 Structure and 1 Stress, `applyNpcStatBonuses`); the rest
 * of their text is read here, off the carrier's frozen `npcFeatureEffectIds`, where a Grunt Trait
 * rides:
 *
 * - **Chaff** ({@link CHAFF_GRUNT}): "they never take damage when they succeed on a save –
 *   regardless of the weapon or system used – but otherwise take damage normally." A passed
 *   `force_save` runs its `on_pass` branch with the saver's damage dropped
 *   (`interpretForceSaveRoll`), and a passed mine save deals nothing instead of half
 *   (`mine.ts`).
 * - **Weak** ({@link WEAK_GRUNT}): "they are immediately destroyed when they take heat from any
 *   source other than their own systems and weapons." Heat that lands from another combatant or
 *   the GM appends a `destroy` step ({@link foreignHeatDestroy}) at each place heat is dealt to
 *   a target: an attack's Heat damage and the GM's (`routeFinalDamage`), an `add_heat` atom
 *   carried by someone else, and a Jockey's Shred. The Grunt's own Heat (Self), Overcharge and
 *   self-targeted `add_heat` never pass a foreign source.
 */
import { hasStatus, type Combatant, type DestroyStep, type ResolvedStep } from "@lancer-vtt/domain";

export const CHAFF_GRUNT = "npcf_chaff_grunt";
export const WEAK_GRUNT = "npcf_weak_grunt";

/** Does `c` carry the Chaff Grunt Trait, so a successful save costs it no damage? */
export function saveNegatesDamage(c: Combatant): boolean {
  return c.base.npcFeatureEffectIds?.includes(CHAFF_GRUNT) === true;
}

/**
 * The `destroy` step owed when `target` (a Weak Grunt) takes `amount` heat from `from` — another
 * combatant's id, or `"gm"` — or `undefined` when nothing is owed: not a Weak Grunt, no heat, its
 * own heat, already Destroyed, or `steps` already destroy it.
 */
export function foreignHeatDestroy(args: { target: Combatant; amount: number; from: string; steps: readonly ResolvedStep[] }): DestroyStep | undefined {
  const { target, amount, from, steps } = args;
  if (amount <= 0 || from === target.id) return undefined;
  if (target.base.npcFeatureEffectIds?.includes(WEAK_GRUNT) !== true) return undefined;
  if (hasStatus(target.statuses, "Destroyed") || steps.some((s) => s.op === "destroy" && s.target === target.id && s.destroyed)) return undefined;
  return { op: "destroy", target: target.id, destroyed: true };
}
